import React, { useEffect, useMemo, useState } from "react";
import Page from "./ui/Page.jsx";
import CaptchaField from "./ui/CaptchaField.jsx";

export default function Login() {
  // --- initial hooks ---
  const [ready, setReady] = useState(false);
  const [sessionValid, setSessionValid] = useState(false);
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNew, setConfirmNew] = useState("");
  const [captchaChange, setCaptchaChange] = useState("");
  const [mobile, setMobile] = useState("");
  const [captchaReset, setCaptchaReset] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [captchaSignin, setCaptchaSignin] = useState("");
  const [captchaRegister, setCaptchaRegister] = useState("");

  const { next, mode } = useMemo(() => {
    try {
      const u = new URL(window.location.href);
      const n = u.searchParams.get("next");
      const m = (u.searchParams.get("mode") || "signin").toLowerCase();
      return {
        next: n && n.startsWith("/") ? n : "/menu",
        mode: ["change", "reset", "register", "verify"].includes(m) ? m : "signin",
      };
    } catch {
      return { next: "/menu", mode: "signin" };
    }
  }, []);

  // --- run cookie/session check ---
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("/api/auth?action=me-user", {
          credentials: "include",
        });
        const j = await res.json().catch(() => ({}));
        if (j.ok) {
          setSessionValid(true);
        }
      } catch {
        // ignore errors
      } finally {
        setReady(true);
      }
    })();
  }, []);

  if (!ready) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: "#ffffff",
          color: "#6b7280",
          fontFamily: "ui-monospace, Menlo, Consolas, monospace",
          textAlign: "center",
        }}
      >
        <pre
          style={{
            lineHeight: 1.05,
            fontSize: "clamp(6px, 1.6vw, 14px)",
            marginBottom: "1rem",
          }}
        >{` ███████████    █████  █████    █████     █████████      █████████ 
░█░░░███░░░█   ░░███  ░░███    ░░███     ███░░░░░███    ███░░░░░███
░   ░███  ░     ░███   ░███     ░███    ░███    ░░░    ░███    ░░░ 
    ░███        ░███   ░███     ░███    ░░█████████    ░░█████████ 
    ░███        ░███   ░███     ░███     ░░░░░░░░███    ░░░░░░░░███
    ░███        ░███   ░███     ░███     ███    ░███    ███    ░███
    █████       ░░████████      █████   ░░█████████    ░░█████████ 
   ░░░░░         ░░░░░░░░      ░░░░░     ░░░░░░░░░      ░░░░░░░░░  `}</pre>

        <div
          style={{
            fontSize: "14px",
            letterSpacing: "0.08em",
            display: "flex",
            gap: "0.3em",
            alignItems: "center",
          }}
        >
          <span>Checking session</span>
          <span
            style={{
              animation: "blink 1.4s infinite steps(1, start)",
            }}
          >
            ...
          </span>
        </div>

        <style>{`
          @keyframes blink {
            0%, 20% { opacity: 0; }
            50%, 100% { opacity: 1; }
          }
        `}</style>
      </div>
    );
  }

  if (sessionValid) {
    window.location.replace("/menu");
    return null;
  }

  async function postFormJSON(action, payload) {
    const res = await fetch(`/api/auth`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action, ...payload }),
    });
    let data = {};
    try { data = await res.json(); } catch { data = {}; }
    const okish = !!(data && (data.ok === true || data.success === true || data.verified === true));
    if (!res.ok || !okish) {
      const code = data.code || data.error || data.message || res.statusText || "ERR";
      throw new Error(String(code));
    }
    return data;
  }

  async function onSubmitSignin(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await postFormJSON("login-user", { username: identifier, password, captcha: captchaSignin });
      window.location.replace(next);
    } catch (err) {
      setError("Sai tài khoản hoặc mật khẩu / Invalid credentials");
    } finally {
      setLoading(false);
    }
  }

  async function onSubmitRegister(e) {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      await postFormJSON("request-access", { email: identifier, captcha: captchaRegister });
      setSuccess("Yêu cầu đăng ký đã được gửi / Registration request sent. Sau khi được duyệt, hãy vào mục Verify để nhập OTP.");
      setCaptchaRegister("");
    } catch (err) {
      setError("Không thể gửi yêu cầu / Request failed");
    } finally {
      setLoading(false);
    }
  }

  async function onSubmitVerify(e) {
    e.preventDefault();
    setError("");
    setSuccess("");

    const v = validateVerifyInputs();
    if (v) { setError(v); return; }

    setLoading(true);
    try {
      await postFormJSON("verify-otp", { email: identifier, code: otpCode, mobile, password });
      setSuccess("Xác minh thành công! / Verification successful. Bạn có thể đăng nhập.");
      setOtpCode(""); setMobile(""); setPassword("");
      setTimeout(() => { window.location.replace("/login?mode=signin"); }, 1200);
    } catch (err) {
      setError(String(err.message || "OTP không hợp lệ hoặc đã hết hạn / Invalid or expired OTP"));
    } finally { setLoading(false); }
  }

  async function onSubmitChange(e) {
    e.preventDefault();
    setError("");
    if (newPassword !== confirmNew) return setError("Mật khẩu mới không khớp / Passwords do not match");
    setLoading(true);
    try {
      await postFormJSON("change-password", { username: identifier, currentPassword, newPassword, captcha: captchaChange });
      alert("Đổi mật khẩu thành công / Password changed successfully");
      setCurrentPassword(""); setNewPassword(""); setConfirmNew(""); setCaptchaChange("");
    } catch { setError("Không thể đổi mật khẩu / Change failed"); }
    finally { setLoading(false); }
  }

  async function onSubmitReset(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await postFormJSON("reset-to-mobile", { username: identifier, mobile, captcha: captchaReset });
      alert("Đã đặt lại mật khẩu về số điện thoại / Password reset to mobile number");
      setCaptchaReset("");
    } catch { setError("Không thể đặt lại mật khẩu / Reset failed"); }
    finally { setLoading(false); }
  }

  function validateVerifyInputs() {
    if (!/^\d{6}$/.test(String(otpCode || "").trim())) return "OTP phải có 6 chữ số / OTP must be 6 digits";
    if (!/^\+?[0-9]{6,15}$/.test(String(mobile || "").trim())) return "Số điện thoại không hợp lệ / Invalid mobile";
    if (String(password || "").trim().length < 8) return "Mật khẩu tối thiểu 8 ký tự / Password min 8 chars";
    return "";
  }

  return (
    <Page>
      <style>{`
        .login-wrap { max-width: 560px; margin: 0 auto; padding: 24px 16px 56px; text-align: left; }
        @media (min-width: 768px) { .login-wrap { max-width: 680px; } }
        @media (min-width: 1024px) { .login-wrap { max-width: 800px; } }
        .ascii-container { display: flex; justify-content: center; margin-bottom: 16px; }
        .ascii { color: #6b7280; text-align: center; }
        .ascii pre { margin: 0 auto; line-height: 1.02; font-family: ui-monospace, Menlo, Consolas, monospace; text-align: center; font-size: clamp(6px, 1.8vw, 14px); display: inline-block; white-space: pre; }
        .section { width: 100%; max-width: 640px; margin: 0 auto; }
        @media (min-width: 1024px) { .section { max-width: 720px; } }
        .field { margin-top: 14px; width: 100%; max-width: 520px; text-align: left; margin-left: auto; margin-right: auto; }
        .label { display:block; font-size: 14px; font-weight: 600; margin-bottom: 6px; }
        .input { width:100%; box-sizing:border-box; border:1px solid #cbd5e1; border-radius:12px; padding:12px 14px; font-size:16px; background:#ffffff; color:#111827; }
        .btn { width:100%; margin: 12px auto 0; border-radius: 12px; padding: 12px 16px; font-weight:700; border:1px solid #94a3b8; background:#f8fafc; max-width: 520px; display: block; }
        .links { display:flex; flex-wrap: wrap; justify-content: space-between; gap:12px; margin-top:20px; font-size: 14px; max-width: 520px; margin-left:auto; margin-right:auto; }
      `}</style>

      <div className="login-wrap">
        <div className="ascii-container">
          <div className="ascii">
            <pre>{` ███████████    █████  █████    █████     █████████      █████████ 
░█░░░███░░░█   ░░███  ░░███    ░░███     ███░░░░░███    ███░░░░░███
░   ░███  ░     ░███   ░███     ░███    ░███    ░░░    ░███    ░░░ 
    ░███        ░███   ░███     ░███    ░░█████████    ░░█████████ 
    ░███        ░███   ░███     ░███     ░░░░░░░░███    ░░░░░░░░███
    ░███        ░███   ░███     ░███     ███    ░███    ███    ░███
    █████       ░░████████      █████   ░░█████████    ░░█████████ 
   ░░░░░         ░░░░░░░░      ░░░░░     ░░░░░░░░░      ░░░░░░░░░  `}</pre>
          </div>
        </div>

        {/* -------------------- SIGN IN -------------------- */}
        {mode === "signin" && (
          <form onSubmit={onSubmitSignin} autoComplete="on" className="section">
            {error && <div className="err" style={{maxWidth:520, margin:"8px auto", color:"#dc2626"}}>{error}</div>}
            <div className="field">
              <label className="label">Email đăng nhập</label>
              <input className="input" value={identifier} onChange={(e) => setIdentifier(e.target.value)} required />
            </div>
            <div className="field">
              <label className="label">Mật khẩu / Password</label>
              <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            <div className="field">
              <CaptchaField purpose="signin" value={captchaSignin} onChange={setCaptchaSignin} />
            </div>
            <button className="btn" disabled={loading}>{loading ? "Đang đăng nhập…" : "Đăng nhập / Sign in"}</button>
            <div className="links">
              <a className="inline" href="/login?mode=register">Đăng ký / Register</a>
              <a className="inline" href="/login?mode=verify">Xác minh OTP / Verify OTP</a>
              <a className="inline" href="/login?mode=change">Đổi mật khẩu / Change password</a>
            </div>
          </form>
        )}

        {/* ------------------- REGISTER ------------------- */}
        {mode === "register" && (
          <form onSubmit={onSubmitRegister} autoComplete="off" className="section">
            {error && <div className="err" style={{maxWidth:520, margin:"8px auto", color:"#dc2626"}}>{error}</div>}
            {success && <div className="success" style={{maxWidth:520, margin:"8px auto", color:"#16a34a"}}>{success}</div>}
            <div className="field">
              <label className="label">Email đăng ký / Registration Email</label>
              <input className="input" type="email" value={identifier} onChange={(e) => setIdentifier(e.target.value)} required />
            </div>
            <div className="field">
              <CaptchaField purpose="register" value={captchaRegister} onChange={setCaptchaRegister} />
            </div>
            <button className="btn" disabled={loading}>{loading ? "Đang gửi…" : "Gửi yêu cầu / Request Access"}</button>
            <div className="links">
              <a href="/login?mode=signin">Quay lại / Back</a>
              <a href="/login?mode=verify">Nhập mã OTP / Verify OTP</a>
            </div>
          </form>
        )}

        {/* -------------------- VERIFY -------------------- */}
        {mode === "verify" && (
          <form onSubmit={onSubmitVerify} autoComplete="off" className="section">
            {error && <div className="err" style={{maxWidth:520, margin:"8px auto", color:"#dc2626"}}>{error}</div>}
            {success && <div className="success" style={{maxWidth:520, margin:"8px auto", color:"#16a34a"}}>{success}</div>}
            <div className="field">
              <label className="label">Email</label>
              <input className="input" type="email" value={identifier} onChange={(e)=>setIdentifier(e.target.value)} required />
            </div>
            <div className="field">
              <label className="label">Mã OTP / Verification code</label>
              <input className="input" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} placeholder="6 digits" value={otpCode} onChange={(e)=>setOtpCode(e.target.value.replace(/\D+/g, '').slice(0,6))} required />
            </div>
            <div className="field">
              <label className="label">Số điện thoại / Mobile</label>
              <input className="input" type="tel" placeholder="e.g. +84901234567" value={mobile} onChange={(e) => setMobile(e.target.value)} required />
            </div>
            <div className="field">
              <label className="label">Mật khẩu / Password</label>
              <input className="input" type="password" placeholder="min 8 characters" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" required />
            </div>
            <button className="btn" disabled={loading}>{loading ? "Đang xác minh…" : "Xác minh / Verify"}</button>
            <div className="links">
              <a href="/login?mode=signin">Đăng nhập / Sign in</a>
              <a href="/login?mode=register">Đăng ký / Register</a>
            </div>
          </form>
        )}

        {/* -------------------- CHANGE -------------------- */}
        {mode === "change" && (
          <form onSubmit={onSubmitChange} autoComplete="off" className="section">
            {error && <div className="err" style={{maxWidth:520, margin:"8px auto", color:"#dc2626"}}>{error}</div>}
            <div className="field">
              <label className="label">Tên đăng nhập / Username</label>
              <input className="input" value={identifier} onChange={(e) => setIdentifier(e.target.value)} required />
            </div>
            <div className="field">
              <label className="label">Mật khẩu hiện tại / Current password</label>
              <input className="input" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
            </div>
            <div className="field">
              <label className="label">Mật khẩu mới / New password</label>
              <input className="input" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
            </div>
            <div className="field">
              <label className="label">Xác nhận mật khẩu mới / Confirm new password</label>
              <input className="input" type="password" value={confirmNew} onChange={(e) => setConfirmNew(e.target.value)} required />
            </div>
            <div className="field">
              <CaptchaField purpose="change" value={captchaChange} onChange={setCaptchaChange} />
            </div>
            <button className="btn" disabled={loading}>{loading ? "Đang xử lý…" : "Đổi mật khẩu / Change"}</button>
            <div className="links">
              <a href="/login?mode=signin">Quay lại / Back</a>
              <a href="/login?mode=reset">Quên mật khẩu? / Reset</a>
            </div>
          </form>
        )}

        {/* --------------------- RESET -------------------- */}
        {mode === "reset" && (
          <form onSubmit={onSubmitReset} autoComplete="off" className="section">
            {error && <div className="err" style={{maxWidth:520, margin:"8px auto", color:"#dc2626"}}>{error}</div>}
            <div className="field">
              <label className="label">Tên đăng nhập / Username</label>
              <input className="input" value={identifier} onChange={(e) => setIdentifier(e.target.value)} required />
            </div>
            <div className="field">
              <label className="label">Số điện thoại / Registered mobile</label>
              <input className="input" type="tel" value={mobile} onChange={(e) => setMobile(e.target.value)} required />
            </div>
            <div className="field">
              <CaptchaField purpose="reset" value={captchaReset} onChange={setCaptchaReset} />
            </div>
            <button className="btn" disabled={loading}>{loading ? "Đang xử lý…" : "Đặt lại / Reset"}</button>
            <div className="links">
              <a href="/login?mode=signin">Quay lại / Back</a>
            </div>
          </form>
        )}
      </div>
    </Page>
  );
}
